// ─────────────────────────────────────────────────────────────
// TodaysPerformanceCard
//
// Daily drawdown monitor for prop firm futures traders.
// Always shows MDD limit + headroom; empty state when no trades.
// ~30% smaller than the previous version.
// ─────────────────────────────────────────────────────────────

import { Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";
import { fmt$, getActiveSessionDate } from "../../lib/format";

export function TodaysPerformanceCard({ trades = [], maxDailyDrawdown = 1500, startingBalance = 0 }) {
  // "Today" = the CME futures session currently open. After 6 PM local
  // time, the session rolls to tomorrow's date — that's what the trader
  // is currently working in. See getActiveSessionDate() for details.
  const today = getActiveSessionDate();

  const todaysTrades = trades.filter(t => {
    if (!t.trade_date) return false;
    // trade_date may be "2026-05-14" or "2026-05-14T..." — just compare the first 10 chars
    return String(t.trade_date).slice(0, 10) === today;
  });

  // Sort chronologically by entry_time. entry_time is just "HH:MM:SS"
  // so we sort lexicographically — which works for that format.
  const sortedTrades = [...todaysTrades].sort((a, b) => {
    const aTime = String(a.entry_time || "");
    const bTime = String(b.entry_time || "");
    return aTime.localeCompare(bTime);
  });

  // Net contribution of one trade to the account balance:
  //   gross pnl - fees - commissions
  // We use net here (not raw pnl) so this card matches the Today P&L
  // KPI tile and the Account Equity card.
  const tradeNet = (t) =>
    (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);

  const todayPnL = sortedTrades.reduce((s, t) => s + tradeNet(t), 0);

  // Compute intraday running P&L and max drawdown from peak
  let runningPnL = 0;
  let peakPnL    = 0;
  let maxDrawdown = 0;
  const chartData = sortedTrades.map(t => {
    runningPnL += tradeNet(t);
    peakPnL = Math.max(peakPnL, runningPnL);
    const dd = peakPnL - runningPnL;
    maxDrawdown = Math.max(maxDrawdown, dd);
    // entry_time is "HH:MM:SS" — just take "HH:MM" for the label
    const timeLabel = String(t.entry_time || "").slice(0, 5) || "—";
    return {
      time: timeLabel,
      pnl: runningPnL,
    };
  });

  // Drawdown headroom: how much loss is allowed before hitting MDD
  // If todayPnL is positive, full MDD is available
  // If todayPnL is negative, we've already used some of the buffer
  const drawdownUsed   = Math.max(0, -todayPnL);
  const drawdownRoom   = Math.max(0, maxDailyDrawdown - drawdownUsed);
  const drawdownPct    = Math.min(100, (drawdownUsed / maxDailyDrawdown) * 100);
  const isWarning      = drawdownPct >= 50 && drawdownPct < 80;
  const isDanger       = drawdownPct >= 80;

  const pnlColor = todayPnL > 0 ? C.green : todayPnL < 0 ? C.red : C.muted;

  // Chart Y-domain: ensures the MDD floor line is always visible
  const yMin = -maxDailyDrawdown - 50;
  const yMax = Math.max(50, peakPnL + 50);

  const pctOfBalance = startingBalance > 0
    ? ((todayPnL / startingBalance) * 100).toFixed(2)
    : null;

  return (
    <Card>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <SectionTitle>Today's Performance</SectionTitle>
        <div style={{
          fontSize: 10,
          color: C.muted,
          fontFamily: "monospace",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}>
          MDD: {fmt$(maxDailyDrawdown)}
        </div>
      </div>

      {/* Top row: P&L and headroom badge */}
      <div style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 10,
        flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <div style={{
            fontSize: 24,
            fontWeight: 800,
            color: pnlColor,
            fontFamily: "monospace",
            letterSpacing: "-0.02em",
          }}>
            {fmt$(todayPnL)}
          </div>
          {pctOfBalance !== null && (
            <div style={{ fontSize: 12, color: C.muted, fontFamily: "monospace" }}>
              {todayPnL >= 0 ? "+" : ""}{pctOfBalance}%
            </div>
          )}
        </div>

        {/* Drawdown headroom badge */}
        <DrawdownBadge
          room={drawdownRoom}
          isWarning={isWarning}
          isDanger={isDanger}
        />
      </div>

      {/* Chart or empty state */}
      {sortedTrades.length > 0 ? (
        <div style={{ height: 130 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="time"
                stroke={C.muted}
                style={{ fontSize: 10 }}
                tick={{ fill: C.muted }}
              />
              <YAxis
                stroke={C.muted}
                style={{ fontSize: 10 }}
                tick={{ fill: C.muted }}
                domain={[yMin, yMax]}
                tickFormatter={(v) => v === 0 ? "$0" : v > 0 ? `+$${v}` : `-$${Math.abs(v)}`}
                width={50}
              />
              <Tooltip
                formatter={(v) => fmt$(v)}
                contentStyle={{
                  background: C.panel,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  fontSize: 11,
                }}
              />
              {/* MDD floor line — always visible */}
              <ReferenceLine
                y={-maxDailyDrawdown}
                stroke={C.red}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: `MDD: -${fmt$(maxDailyDrawdown).replace("$", "$")}`,
                  position: "insideBottomRight",
                  fill: C.red,
                  fontSize: 9,
                }}
              />
              {/* Zero line for reference */}
              <ReferenceLine y={0} stroke={C.border} strokeWidth={1} />
              <Line
                type="monotone"
                dataKey="pnl"
                stroke={pnlColor}
                strokeWidth={2}
                dot={{ fill: pnlColor, r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyState maxDailyDrawdown={maxDailyDrawdown} />
      )}

      {/* Bottom: progress bar (only shown when we've actually used some drawdown) */}
      {drawdownUsed > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 10,
            color: C.muted,
            marginBottom: 4,
          }}>
            <span>Drawdown used</span>
            <span style={{ fontFamily: "monospace" }}>
              {fmt$(drawdownUsed)} / {fmt$(maxDailyDrawdown)} ({drawdownPct.toFixed(0)}%)
            </span>
          </div>
          <div style={{
            height: 6,
            background: C.border,
            borderRadius: 3,
            overflow: "hidden",
          }}>
            <div style={{
              height: "100%",
              width: `${drawdownPct}%`,
              background: isDanger ? C.red : isWarning ? C.yellow : C.green,
              transition: "width 0.3s ease",
            }} />
          </div>
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────
function DrawdownBadge({ room, isWarning, isDanger }) {
  const color = isDanger ? C.red : isWarning ? C.yellow : C.green;
  const label = isDanger ? "⚠ Near limit" : isWarning ? "Caution" : "Room";
  return (
    <div style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      background: color + "15",
      border: `1px solid ${color}40`,
      borderRadius: 6,
      fontSize: 11,
      whiteSpace: "nowrap",
    }}>
      <span style={{ color, fontWeight: 600 }}>{label}:</span>
      <span style={{ color, fontFamily: "monospace", fontWeight: 700 }}>
        {fmt$(room)}
      </span>
    </div>
  );
}

function EmptyState({ maxDailyDrawdown }) {
  return (
    <div style={{
      height: 130,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      color: C.muted,
      fontSize: 12,
      background: `linear-gradient(180deg, transparent 0%, ${C.red}08 100%)`,
      borderRadius: 6,
      border: `1px dashed ${C.border}`,
    }}>
      <div style={{ fontSize: 22, opacity: 0.5 }}>—</div>
      <div>No trades yet today</div>
      <div style={{ fontSize: 10, color: C.muted, opacity: 0.7 }}>
        Full {fmt$(maxDailyDrawdown)} drawdown buffer available
      </div>
    </div>
  );
}
